import { isStandalone } from '@/lib/device';
import { locale, m } from '@/lib/i18n';

/** An error the user can read: the server's own message, or a friendly one. */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

const TIMEOUT_MS = 15_000;

export async function api<T = unknown>(method: string, path: string, body?: unknown): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  let res: Response;
  try {
    res = await fetch(`/api${path}`, {
      method,
      // x-app lets the device list say "iPhone · App" instead of the browser the app runs in.
      headers: { 'content-type': 'application/json', 'x-locale': locale, 'x-app': isStandalone() ? 'installed' : 'browser' },
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: controller.signal,
      credentials: 'same-origin',
    });
  } catch (err) {
    if ((err as Error).name === 'AbortError') throw new ApiError(m.err_timeout(), 0);
    throw new ApiError(navigator.onLine ? m.err_offline() : m.err_no_connection(), 0);
  } finally {
    clearTimeout(timer);
  }
  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new ApiError(data?.error ?? m.err_generic(), res.status);
  return data as T;
}
